import React from "react";
import { Drawer, Form, Input, Button, Switch, Space, Tooltip, InputNumber } from "antd";
import { EditOutlined, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";

interface DrawerWithFormProps {
  form: any;
  openDrawer: () => void;
  closeDrawer: () => void;
  isOpen: boolean;
  type: string;
  onSubmit: (values: any) => void;
  submitting: boolean;
  buttonLabel: string;
  selectedRows?: number[];
    hasPermissions: boolean;

}

const CurrenciesRequestForm: React.FC<DrawerWithFormProps> = ({
  form,
  openDrawer,
  closeDrawer,
  isOpen,
  type,
  onSubmit,
  submitting,
  buttonLabel,
  selectedRows,
  hasPermissions,
}) => {
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      console.log("values submitting is ",values)
      onSubmit(values);
    } catch (error) {
      console.error("Validation Failed:", error);
    }
  };

  return (
    <>
      <Button
        type="primary"
        icon={buttonLabel === 'Edit' ? <EditOutlined /> : <PlusOutlined />}
        className="action-btn"
        onClick={openDrawer}
        disabled={!hasPermissions}
      >
        {buttonLabel === "Edit" ? "Edit" : "Add Currency"}
      </Button>

      <Drawer
              title={buttonLabel === "Edit" ? "Edit Currency" : "Add Currency"}
              placement="right"
              onClose={closeDrawer}
              open={isOpen}
              width={600}
              closable={true}
              className="manage-pages-drawer"
              forceRender
            >
                <Form form={form} autoComplete="off">
                  <Form.Item label="Name">
                    <Space>
                      <Form.Item
                        name="name"
                        noStyle
                        rules={[
                          {
                            required: true,
                            message:
                              "Please enter the name of the code, for example 'USD'",
                          },
                        ]}
                      >
                        <Input placeholder="Name" maxLength={3} />
                      </Form.Item>
                      <Tooltip
                        title="The name of the code, for example 'USD'"
                        color={"var(--tertiary-color)"}
                        placement={"bottom"}
                      >
                        <InfoCircleOutlined
                          style={{ color: "var(--tertiary-color)" }}
                        />
                      </Tooltip>
                    </Space>
                  </Form.Item>
      
                  <Form.Item label="Symbol">
                    <Space>
                      <Form.Item
                        name="symbol"
                        noStyle
                      >
                        <Input maxLength={3} placeholder="$" style={{ width: 100 }} />
                      </Form.Item>
                      <Tooltip
                        title="The symbol to use for the currency, for example: '$'"
                        color={"var(--tertiary-color)"}
                        placement={"bottom"}
                      >
                        <InfoCircleOutlined
                          style={{ color: "var(--tertiary-color)" }}
                        />
                      </Tooltip>
                    </Space>
                  </Form.Item>
      
                  <Form.Item label="Decimal Character">
                    <Space>
                      <Form.Item
                        name="decimalCharacter"
                        noStyle
                      >
                        <Input maxLength={3} placeholder="." style={{ width: 100 }} />
                      </Form.Item>
                      <Tooltip
                        title="The character used to separate between the whole number and the decimal fraction, for example: '.'"
                        color={"var(--tertiary-color)"}
                        placement={"bottom"}
                      >
                        <InfoCircleOutlined
                          style={{ color: "var(--tertiary-color)" }}
                        />
                      </Tooltip>
                    </Space>
                  </Form.Item>
      
                  <Form.Item label="Separator Character">
                    <Space>
                      <Form.Item
                        name="separatorCharacter"
                        noStyle
                      >
                        <Input maxLength={3} placeholder="," style={{ width: 100 }} />
                      </Form.Item>
                      <Tooltip
                        title="The character used to separate thousands in the number, for example: ','"
                        color={"var(--tertiary-color)"}
                        placement={"bottom"}
                      >
                        <InfoCircleOutlined
                          style={{ color: "var(--tertiary-color)" }}
                        />
                      </Tooltip>
                    </Space>
                  </Form.Item>
      
                  <Form.Item label="Precision">
                    <Space>
                      <Form.Item
                        name="precision"
                        noStyle
                      >
                        <InputNumber
                          maxLength={999}
                          placeholder="2"
                          min={0}
                          max={100}
                          precision={0}
                          style={{ width: 100 }}
                        />
                      </Form.Item>
                      <Tooltip
                        title="The number of numbers to show after the decimal point, some currencies do not support fractions of values. "
                        color={"var(--tertiary-color)"}
                        placement={"bottom"}
                      >
                        <InfoCircleOutlined
                          style={{ color: "var(--tertiary-color)" }}
                        />
                      </Tooltip>
                    </Space>
                  </Form.Item>
      
                  <Form.Item label="Display Pattern">
                    <Space>
                      <Form.Item
                        name="displayPattern"
                        noStyle
                        rules={[
                          {
                            required: false,
                            pattern: new RegExp("^[!.#]*$"),
                            message:
                              "Please enter the display pattern as to where to display the currency symbol and value, for example: '!#' where ! is the symbol and # the value",
                          },
                        ]}
                      >
                        <Input
                          maxLength={2}
                          placeholder="!#"
                          style={{ width: 100 }}
                        />
                      </Form.Item>
                      <Tooltip
                        title="The display pattern as to where to display the currency symbol and value, for example: '!#' where ! is the symbol and # the value"
                        color={"var(--tertiary-color)"}
                        placement={"bottom"}
                      >
                        <InfoCircleOutlined
                          style={{ color: "var(--tertiary-color)" }}
                        />
                      </Tooltip>
                    </Space>
                  </Form.Item>
      
                  <Form.Item label="Negative Display Pattern">
                    <Space>
                      <Form.Item
                        name="negativeDisplayPattern"
                        noStyle
                        rules={[
                          {
                            required: false,
                            pattern: new RegExp("^[-.!.#]*$"),
                            message:
                              "Please enter the display pattern as to where to display the currency symbol and value for negative numbers, for example: '-!#' where ! is the symbol and # the value",
                          },
                        ]}
                      >
                        <Input
                          maxLength={3}
                          placeholder="-!#"
                          style={{ width: 100 }}
                        />
                      </Form.Item>
                      <Tooltip
                        title="The display pattern as to where to display the currency symbol and value for negative numbers, for example: '-!#' where ! is the symbol and # the value"
                        color={"var(--tertiary-color)"}
                        placement={"bottom"}
                      >
                        <InfoCircleOutlined
                          style={{ color: "var(--tertiary-color)" }}
                        />
                      </Tooltip>
                    </Space>
                  </Form.Item>
      
                  <Form.Item
                    label="Active"
                    name="isActive"
                    valuePropName="checked"
                  >
                    <Switch defaultChecked={true} />
                  </Form.Item>
      
                  <Form.Item>
                    <Button type="primary" onClick={handleSubmit} loading={submitting}>
                      Save Currency
                    </Button>
                  </Form.Item>
                </Form>
              
            </Drawer>
    </>
  );
};

export default CurrenciesRequestForm;
